import { useMemo, useState } from "react";
import { Button, Col, Divider, Modal, Row, Space, Tag, Typography } from "antd";
import { pdf } from "@react-pdf/renderer";
import {
  LeaveApplicationPDFRenderer,
  LeaveApplicationPDFTemplateType,
} from "../../context/nonVisual/LeaveApplicationPDF";
import { formatDate } from "../../utils/helpers";

type PdfDebugCase = {
  key: string;
  label: string;
  notes: string;
  name: string;
  position: string;
  days: number;
  startDate: Date;
  endDate: Date;
  requestYear: number;
  substitute: string;
  employeeSignature: string;
  managerSignature: string;
  gender?: string;
  previousYearDaysUsedForRequest: number;
  currentYearDaysUsedForRequest: number;
};

const buildPeriod = (startDate: Date, endDate: Date) => {
  const formattedStart = formatDate(startDate);
  const formattedEnd = formatDate(endDate);
  return formattedStart === formattedEnd
    ? formattedStart
    : `${formattedStart} — ${formattedEnd}`;
};

const buildTemplate = (pdfCase: PdfDebugCase): LeaveApplicationPDFTemplateType =>
  pdfCase.previousYearDaysUsedForRequest > 0 &&
  pdfCase.currentYearDaysUsedForRequest > 0
    ? "mixed-year"
    : "single-year";

const debugCases: PdfDebugCase[] = [
  {
    key: "current-multi",
    label: "Current Year / Multiple Days",
    notes: "No split sentence should appear.",
    name: "Alex Popescu",
    position: "Software Engineer",
    days: 5,
    startDate: new Date("2026-06-08"),
    endDate: new Date("2026-06-12"),
    requestYear: 2026,
    substitute: "Maria Ionescu",
    employeeSignature: "Semnătură angajat (mock)",
    managerSignature: "Semnătură manager (mock)",
    gender: "male",
    previousYearDaysUsedForRequest: 0,
    currentYearDaysUsedForRequest: 5,
  },
  {
    key: "current-single",
    label: "Current Year / 1 Day",
    notes: "Must use Romanian singular: 1 zi, data de.",
    name: "Ioana Dumitru",
    position: "QA Engineer",
    days: 1,
    startDate: new Date("2026-07-14"),
    endDate: new Date("2026-07-14"),
    requestYear: 2026,
    substitute: "Vlad Stan",
    employeeSignature: "Semnătură angajat (mock)",
    managerSignature: "Semnătură manager (mock)",
    gender: "female",
    previousYearDaysUsedForRequest: 0,
    currentYearDaysUsedForRequest: 1,
  },
  {
    key: "prev-only-multi",
    label: "Previous Year Only / Multiple Days",
    notes: "Split sentence should appear with Y=0 for current year.",
    name: "Mihai Georgescu",
    position: "Consultant",
    days: 3,
    startDate: new Date("2026-01-20"),
    endDate: new Date("2026-01-22"),
    requestYear: 2026,
    substitute: "Elena Marin",
    employeeSignature: "Semnătură angajat (mock)",
    managerSignature: "Semnătură manager (mock)",
    gender: "male",
    previousYearDaysUsedForRequest: 3,
    currentYearDaysUsedForRequest: 0,
  },
  {
    key: "prev-only-single",
    label: "Previous Year Only / 1 Day",
    notes: "Split sentence should use singular for previous-year part.",
    name: "Raluca Pavel",
    position: "Business Analyst",
    days: 1,
    startDate: new Date("2026-02-02"),
    endDate: new Date("2026-02-02"),
    requestYear: 2026,
    substitute: "Andrei Matei",
    employeeSignature: "Semnătură angajat (mock)",
    managerSignature: "Semnătură manager (mock)",
    gender: "female",
    previousYearDaysUsedForRequest: 1,
    currentYearDaysUsedForRequest: 0,
  },
  {
    key: "mixed-multi",
    label: "Mixed Year / Multiple Days",
    notes: "Split sentence should show both previous/current values.",
    name: "Diana Ene",
    position: "Project Manager",
    days: 6,
    startDate: new Date("2026-03-16"),
    endDate: new Date("2026-03-23"),
    requestYear: 2026,
    substitute: "Sorin Radu",
    employeeSignature: "Semnătură angajat (mock)",
    managerSignature: "Semnătură manager (mock)",
    gender: "female",
    previousYearDaysUsedForRequest: 2,
    currentYearDaysUsedForRequest: 4,
  },
  {
    key: "mixed-singular",
    label: "Mixed Year / Singular Branch",
    notes: "One split branch singular (ex: 1 zi), one plural (ex: 2 zile).",
    name: "Cristian Toma",
    position: "Senior Consultant",
    days: 3,
    startDate: new Date("2026-04-06"),
    endDate: new Date("2026-04-08"),
    requestYear: 2026,
    substitute: "Ana Luca",
    employeeSignature: "Semnătură angajat (mock)",
    managerSignature: "Semnătură manager (mock)",
    gender: "male",
    previousYearDaysUsedForRequest: 1,
    currentYearDaysUsedForRequest: 2,
  },
  {
    key: "gender-neutral",
    label: "Gender Neutral Text",
    notes: "Checks neutral copy: Subsemnatul/a, angajat/ă.",
    name: "Robin Debug",
    position: "Designer",
    days: 2,
    startDate: new Date("2026-08-10"),
    endDate: new Date("2026-08-11"),
    requestYear: 2026,
    substitute: "Pat Mock",
    employeeSignature: "Semnătură angajat (mock)",
    managerSignature: "Semnătură manager (mock)",
    gender: undefined,
    previousYearDaysUsedForRequest: 0,
    currentYearDaysUsedForRequest: 2,
  },
];

const triggerDownload = async (pdfCase: PdfDebugCase) => {
  const template = buildTemplate(pdfCase);
  const pdfDocument = (
    <LeaveApplicationPDFRenderer
      template={template}
      name={pdfCase.name}
      position={pdfCase.position}
      days={pdfCase.days}
      period={buildPeriod(pdfCase.startDate, pdfCase.endDate)}
      date={new Date()}
      requestYear={pdfCase.requestYear}
      substitute={pdfCase.substitute}
      employeeSignature={pdfCase.employeeSignature}
      managerSignature={pdfCase.managerSignature}
      gender={pdfCase.gender}
      previousYearDaysUsedForRequest={pdfCase.previousYearDaysUsedForRequest}
      currentYearDaysUsedForRequest={pdfCase.currentYearDaysUsedForRequest}
    />
  );

  const blob = await pdf(pdfDocument).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `pdf-debug-${pdfCase.key}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const PDFGenerationDebugModal = () => {
  const [open, setOpen] = useState(false);
  const [loadingCaseKey, setLoadingCaseKey] = useState<string | null>(null);

  const cases = useMemo(() => debugCases, []);

  const handleDownload = async (pdfCase: PdfDebugCase) => {
    try {
      setLoadingCaseKey(pdfCase.key);
      await triggerDownload(pdfCase);
    } finally {
      setLoadingCaseKey(null);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>PDF Debug</Button>

      <Modal
        title="PDF Generation Debug Cases"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={980}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            Uses mock data and mock signatures. Download each case to validate wording,
            singular/plural, split sentence logic, and template switching.
          </Typography.Text>
          <Divider style={{ margin: 0 }} />

          {cases.map((pdfCase) => {
            const template = buildTemplate(pdfCase);
            return (
              <div key={pdfCase.key}>
                <Row align="middle" justify="space-between" gutter={[12, 12]}>
                  <Col xs={24} md={16}>
                    <Space size={8} wrap>
                      <Typography.Text strong>{pdfCase.label}</Typography.Text>
                      <Tag color={template === "mixed-year" ? "purple" : "blue"}>
                        {template}
                      </Tag>
                      <Tag>
                        prev: {pdfCase.previousYearDaysUsedForRequest} / curr:{" "}
                        {pdfCase.currentYearDaysUsedForRequest}
                      </Tag>
                    </Space>
                    <div>
                      <Typography.Text type="secondary">
                        {pdfCase.notes}
                      </Typography.Text>
                    </div>
                  </Col>
                  <Col xs={24} md={8} style={{ textAlign: "right" }}>
                    <Button
                      type="primary"
                      onClick={() => handleDownload(pdfCase)}
                      loading={loadingCaseKey === pdfCase.key}
                    >
                      Download PDF
                    </Button>
                  </Col>
                </Row>
                <Divider />
              </div>
            );
          })}
        </Space>
      </Modal>
    </>
  );
};

export default PDFGenerationDebugModal;
